import React, { useState, useEffect } from 'react';
import { Center, Stack } from '@fiverr-private/layout_components';
import { Typography } from '@fiverr-private/typography';
import { Button } from '@fiverr-private/button';
import { Video } from '@fiverr-private/media';
import { Input } from '@fiverr-private/forms';
import { ExternalLinkIcon, ErrorCircleIcon } from '@fiverr-private/visuals';
import { getContext } from '@fiverr-private/fiverr_context';
import { ElementType } from '@fiverr-private/bianka_js';
import { useImpressionContainer, useImpressionItem } from '@fiverr-private/impressionable';
import { stepMetaMap, onImpression } from '@fiverr-private/store_builder_bi_events';
import { ListItem, NumberedList } from '@fiverr-private/list';
import { AppI18n } from '../../i18n';
import { useStoreData } from '../../hooks/useStoreData';
import { useAutoSave } from '../../hooks/useAutoSave';
import { useStepValidation } from '../../hooks/use-step-validation/useStepValidation';
import { verifyStoreLink } from '../../services/verifyStoreLink';
import { useWizardNavigation } from '../../hooks/use-wizard-navigation/useWizardNavigation';
import { reportClickEvent } from '../../utils/report-bi-event/reportClickEvent';
import { GifWrapper } from '../../components/gif-wrapper/GifWrapper';
import { StepTitle } from '../../components/step-title/StepTitle';
import { UserNotAuthorizedError } from '../../errors/userNotAuthorized';
import { GIF_URL, SHOPIFY_URL } from './LinkYourStore.constants';

const LIST_ITEMS = [
    {
        id: 1,
        text: (
            <AppI18n k="steps.link_your_store.list.item_1" templates={{ strong: (text) => <strong>{text}</strong> }} />
        ),
    },
    {
        id: 2,
        text: (
            <AppI18n k="steps.link_your_store.list.item_2" templates={{ strong: (text) => <strong>{text}</strong> }} />
        ),
    },
];

export const LinkYourStore: React.FC = () => {
    const { pageCtxId, userId } = getContext();
    const { currentPath } = useWizardNavigation();
    const { storeData } = useStoreData();
    const { autoSave } = useAutoSave();
    const { registerValidationOnNext, unregisterValidationOnNext } = useStepValidation();
    const [shopifyUrl, setShopifyUrl] = useState(storeData?.storeUrl || '');
    const [isShopifyUrlValid, setIsShopifyUrlValid] = useState<boolean | null>(null);
    const [pageImpressionRef, pageImpressionId] = useImpressionContainer();
    const shouldShowError = isShopifyUrlValid === false;

    useImpressionItem({
        selfRef: pageImpressionRef,
        selfId: pageImpressionId,
        sendImpressionToBQ: false,
        enrichment: {
            eventType: stepMetaMap[currentPath].impressionName,
            componentName: stepMetaMap[currentPath].name,
        },
        intersectionThreshold: 0,
        onImpression,
    });

    useEffect(() => {
        const validationStatusFn = async () => {
            if (!storeData?.id) return 'invalid';

            try {
                const isValid = await verifyStoreLink(storeData.id, { storeUrl: shopifyUrl });
                setIsShopifyUrlValid(isValid);

                return isValid ? 'valid' : 'invalid';
            } catch (error) {
                setIsShopifyUrlValid(false);

                if (error instanceof UserNotAuthorizedError) {
                    return 'unauthenticated';
                }
                return 'invalid';
            }
        };

        registerValidationOnNext('link-your-store', validationStatusFn);
        return () => {
            unregisterValidationOnNext('link-your-store');
        };
    }, [registerValidationOnNext, unregisterValidationOnNext, storeData?.id, shopifyUrl]);

    useEffect(() => {
        if (shopifyUrl && shopifyUrl !== storeData?.storeUrl) {
            const timeoutId = setTimeout(() => {
                autoSave({ storeUrl: shopifyUrl });
            }, 1000);

            return () => clearTimeout(timeoutId);
        }
    }, [shopifyUrl, storeData?.storeUrl, autoSave]);

    const handleAccessShopifyClick = () => {
        reportClickEvent({
            biEventData: {
                userId,
                ctxId: pageCtxId,
            },
            eventType: 'clicked_on_access_shopify',
            elementType: ElementType.LINK,
            currentPath,
        });
        window.open(SHOPIFY_URL, '_blank');
    };

    return (
        <Center>
            <Stack
                direction="column"
                gap="6"
                color="grey_1200"
                ref={pageImpressionRef}
                width={{ default: 310, sm: 488 }}
            >
                <Stack direction="column" gap="2">
                    <StepTitle title={<AppI18n k="steps.link_your_store.title" />} />
                    <NumberedList rowGap="4" paddingLeft="5">
                        {LIST_ITEMS.map(({ id, text }) => (
                            <ListItem key={id} marginBottom="2">
                                {text}
                            </ListItem>
                        ))}
                    </NumberedList>
                </Stack>
                <GifWrapper marginTop="2">
                    <Video
                        sources={[
                            {
                                type: 'video/mp4',
                                src: GIF_URL,
                            },
                        ]}
                        autoPlay
                        loop
                        muted
                        playsInline
                        controls={false}
                        width={{ default: 310, sm: 488 }}
                    />
                </GifWrapper>
                <Button onClick={handleAccessShopifyClick} alignSelf="flexStart" variant="outline">
                    <AppI18n k="steps.link_your_store.cta" />
                    <ExternalLinkIcon />
                </Button>

                <Stack direction="column" gap="1.5" marginTop="2">
                    <label htmlFor="shopifyUrl">
                        <AppI18n k="steps.link_your_store.form_label" />
                    </label>
                    <Input
                        id="shopifyUrl"
                        type="url"
                        placeholder="Ex: https://admin.shopify.com/store/test"
                        value={shopifyUrl}
                        onChange={(e) => setShopifyUrl(e.target.value)}
                        style={{
                            borderColor: shouldShowError ? 'red' : 'initial',
                            padding: '8px',
                            borderRadius: '4px',
                            width: '100%',
                        }}
                    />
                    {shouldShowError && (
                        <Typography color="red_1000">
                            <Stack gap="1">
                                <ErrorCircleIcon size="md" />
                                <AppI18n k="steps.link_your_store.form_error" />
                            </Stack>
                        </Typography>
                    )}
                </Stack>
            </Stack>
        </Center>
    );
};

LinkYourStore.displayName = 'LinkYourStore';
